/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// region Hooks
import { useSelectClient } from "@atoms/SelectClient";
import { useAuth } from "src/hooks/useAuth";
// endregion Hooks
// region Molecules
import { BaseCard, BaseCardHeader, BaseCardItem, PageHeader } from "@molecules/index";
// endregion Molecules
// region Atoms
import { Loading } from "@atoms/index";
import ShowMoreButton from "@atoms/ShowMoreButton";
import AlertContentByClient from "@atoms/AlertContentByClient";
// endregion Atoms
// region Store
import Store from "@store/jotai";
// endregion Store
// region Graphql
import {
  contractAllWithSortedPagination,
  TContractAllWithSortedPaginationQueries
} from "@graphql/groups/contract/query.graphql";
import {
  ContractQueriesGetAllWithSortedPaginationArgs,
  ContractType,
  ECommandResultStatus,
  ESort,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
// endregion Graphql
// region Constants
import { ModulesID } from "src/store/constants";
// endregion Constants
// region Libraries
import { Grid } from "@libraries/mui";
// endregion Libraries
// region Languages
import useTranslation from "@languages/useTranslation";
import { AlertMessages, CustomerMessages } from "@languages/interfaces";
import Office from "../Office";
// endregion Languages
// region Styles
import * as Styled from "./styles";
// endregion Styles

const Contract: React.FC = () => {

  const { t } = useTranslation();
  const { value: contracts, setValue: setContracts, filteredContracts, setFilteredContracts } = Store.useContracts();
  const { value: selectedClient } = useSelectClient();

  const [whereContractIds, setWhereContractIds] = useState<string>("");
  const [pricePermission, setPricePermission] = useState<{ [key: string]: { showPrice: boolean } }>({});
  const [nextPage, setNextPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const { addToast } = useToast();
  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();

  const limitPerPage = 12;

  // region Get permissions according to the client
  const {
    loading: loadingPermissions,
    error: errorPermissions,
    data: dataPermissions
  } = useQuery<
    TPermissionAllWithoutPaginationQueries,
    PermissionUserQueriesGetAllWithoutPaginationArgs
  >(permissionUserAllWithoutPagination(["contract.id", "module.id"]), {
    fetchPolicy: "network-only",
    pollInterval: 60000,
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "construction.client.id",
          operator: EWhereClausesOperators.EQUALS,
          value: selectedClient.id
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.IN,
          value: `${ModulesID.CONTRACT} | ${ModulesID.AMOUNTS}`
        }
      ]
    }
  });
  // endregion Get permissions according to the client
  // region Get contracts according to the client
  const [
    getContracts,
    { loading: loadingContracts, error: errorContracts, data: dataContracts }
  ] = useLazyQuery<
    TContractAllWithSortedPaginationQueries,
    ContractQueriesGetAllWithSortedPaginationArgs
  >(
    contractAllWithSortedPagination([
      "id",
      "status",
      "code",
      "contractDate",
      "contractConcreteRecipes.id",
      "contractConcreteRecipes.status",
      "contractConcreteRecipes.m3Value",
      "contractConcreteRecipes.totalVolume",
      "contractConcreteRecipes.deliveredVolume",
      "contractConcreteRecipes.concreteRecipe.description",
      "construction.name",
      "construction.address",
      "construction.county",
      "construction.number",
      "construction.complement",
      "contractSellers.seller.name"
    ]),
    {
      fetchPolicy: "network-only",
      pollInterval: 60000
    }
  );
  // endregion Get contracts according to the client

  /**
   * Load contracts
   */
  const loadContracts = useCallback(async () => {

    const response = dataContracts?.contract.getAllWithSortedPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      setTotalPages(response.pageCount);

      const contractsList = response.result;

      if (contractsList && contractsList.length > 0) {

        contractsList.forEach((contract) => {

          if (pricePermission[contract!.id]) {
            contract!.showPrice = true;
          }
        });
      }

      if (nextPage > 1) setContracts(contracts.concat(contractsList as ContractType[]));
      else setContracts(contractsList as ContractType[]);
    }
  }, [dataContracts, setContracts]);

  /**
   * Construct where clause for contract ids
   */
  const makeWhereContractIds = useCallback(() => {

    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      const contractIds: string[] = [];

      response.result?.forEach((permission) => {
        contractIds.push(permission?.contract?.id || "");
        const moduleId = permission?.module?.id;

        if (moduleId && moduleId === ModulesID.AMOUNTS) {
          pricePermission[permission!.contract!.id] = {
            showPrice: true
          };
        }
      });

      const contractIdsStr = contractIds
        .filter((id) => id && id !== "")
        .join("|");

      if (!contractIdsStr || contractIdsStr === "") {

        setWhereContractIds("");
        setFilteredContracts([]);

        return;
      }
      setWhereContractIds(contractIdsStr);
    }
  }, [dataPermissions, setFilteredContracts]);

  /**
   * Details contract
   */
  const detailsContract = useCallback((contract: ContractType) => {

    if (!contract.contractConcreteRecipes) return undefined;

    return (
      <div>
        {contract.contractConcreteRecipes.map((contractConcreteRecipe) => {
          if (!contractConcreteRecipe) return undefined;

          return (
            <BaseCardItem
              key={contractConcreteRecipe.id}
              type="contractConcreteRecipe"
              contractConcreteRecipe={contractConcreteRecipe}
              showVolumePrice={pricePermission[contract.id]?.showPrice}
            />
          );
        })}
      </div>
    );
  }, []);

  // Get contracts when where clause is ready
  useEffect(() => {
    if (whereContractIds !== "") {
      getContracts({
        variables: {
          page: nextPage,
          perPage: limitPerPage,
          sort: [{
            field: "contractDate",
            sortDirection: ESort.DESC
          }],
          where: [
            {
              property: "id",
              operator: EWhereClausesOperators.IN,
              value: whereContractIds
            }
          ]
        }
      }).then();
    }
  }, [getContracts, whereContractIds, nextPage]);

  // Make where clause when permissions are ready
  useEffect(() => {
    if (dataPermissions) makeWhereContractIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereContractIds]);

  // Load contracts when they are ready
  useEffect(() => {
    if (dataContracts) loadContracts().then();
    if (errorContracts) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorContracts.message
      });
    }
  }, [addToast, dataContracts, errorContracts, loadContracts]);

  // Reset contracts when client changes
  useEffect(() => {
    setContracts([]);
    setNextPage(1);
  }, [selectedClient, setContracts]);

  // Set filtered contracts when contracts change
  useEffect(() => {
    setFilteredContracts(contracts);
  }, [contracts, setFilteredContracts]);

  // Reset contracts when component unmounts
  useEffect(() => () => {
    setContracts([]);
    setFilteredContracts([]);
  }, [setContracts, setFilteredContracts]);

  const handleNextPage = () => setNextPage(nextPage + 1);

  return (
    <Office>
      <PageHeader url="/" title={t(CustomerMessages.applicationContract)} />
      <Styled.Container>
        <Grid container spacing={2}>
          {(loadingContracts || loadingPermissions) && (
            <Grid item xs={12}>
              <Loading loading />
            </Grid>
          )}
          {!loadingPermissions
            && !loadingContracts
            && filteredContracts?.length === 0 && (
              <Grid item xs={12}>
                <AlertContentByClient alertMessage={t(AlertMessages.noContract)} iconColor="yellow" />
              </Grid>
          )}
          {!loadingPermissions
            && !loadingContracts
            && filteredContracts
            && filteredContracts.map((contract) => (
              <Grid key={`grid-${contract.id}`} item xs={12} sm={6} md={4}>
                <BaseCard
                  key={contract.id}
                  id={contract.id}
                  header={<BaseCardHeader type="contract" data={contract} />}
                  details={detailsContract(contract)}
                />
              </Grid>
            ))}
        </Grid>
        {filteredContracts
          && nextPage < totalPages
          && (
            <Styled.ContainerShowMoreButton>
              <ShowMoreButton onClick={handleNextPage} />
            </Styled.ContainerShowMoreButton>
          )}
      </Styled.Container>
    </Office>
  );
};

export default Contract;
